import {
  BaseDecisionPriority,
  DecisionContext,
  DecisionResult,
  MoveInfo,
} from "..\/..\/interfaces\/DecisionPriority";
import {
  Board,
  Move,
  IPiece,
  Position,
  PieceType,
  MoveType,
} from "..\/..\/..\/..\/types";
import {
  MCTSUtils_v2,
  cloneFullGameState,
  getPieceFromPosition,
  applyMoveWithSnakeRespawn,
  validateMeadowEffects,
} from "..\/..\/..\/utils\/MCTSUtils";
import { MCTSMoveGenerationService } from "..\/..\/..\/utils\/MCTSMoveGenerationService";

/**
 * Priority 9: Hanging Pieces Prevention
 *
 * SIMPLE JOB: Prevent any move that hangs a piece without immediate recapture.
 * This is a pure anti-blunder filter - no compensation logic, no strategic exceptions.
 * Higher priority policies (1-8) handle strategic sacrifices.
 */
export class HangingPiecesPrevention extends BaseDecisionPriority {
  readonly priority = 15;
  readonly name = "Hanging Pieces Prevention";
  readonly description =
    "SIMPLE: Prevent hanging pieces - no compensation logic, pure anti-blunder filter";

  private analysisLogs: string[] = [];

  isRelevant(context: DecisionContext): boolean {
    // Always relevant - we must always check for hanging pieces
    return true;
  }

  analyze(context: DecisionContext): DecisionResult {
    this.analysisLogs = [];

    const gameState = context.gameState;
    const isPlayerTurn = gameState.isLightTurn;
    const playerColor = isPlayerTurn ? "Light" : "Dark";

    this.addLog(
      `🎯 HANGING PREVENTION: Analyzing ${playerColor} hanging risks...`
    );

    // Filter out king moves immediately - they're handled by King Safety priorities
    const nonKingMoves = context.allPossibleMoves.filter((moveInfo) => {
      const piece = MCTSUtils_v2.getPieceAt(
        gameState.board,
        moveInfo.fromPosition
      );
      return piece && !piece.isKing;
    });

    const kingMovesCount =
      context.allPossibleMoves.length - nonKingMoves.length;

    // Find moves that hang pieces (excluding king moves)
    const hangingMoves: MoveInfo[] = [];
    const safeMoves: MoveInfo[] = [];

    for (const moveInfo of nonKingMoves) {
      const hangingCheck = this.isMovePieceHanging(moveInfo, gameState);

      if (hangingCheck.hanging) {
        hangingMoves.push(moveInfo);
      } else {
        safeMoves.push(moveInfo);
      }
    }

    // Add all king moves back to safe moves (they're handled by KingSafety priority)
    const allKingMoves = context.allPossibleMoves.filter((moveInfo) => {
      const piece = MCTSUtils_v2.getPieceAt(
        gameState.board,
        moveInfo.fromPosition
      );
      return piece && piece.isKing;
    });
    safeMoves.push(...allKingMoves);

    // Add concise summary
    this.addLog(`🔍 Hanging analysis: ${nonKingMoves.length} moves checked`);
    if (safeMoves.length - kingMovesCount > 0) {
      this.addLog(
        `✅ ${
          safeMoves.length - kingMovesCount
        } moves safe (not hanging pieces)`
      );
    }
    if (kingMovesCount > 0) {
      this.addLog(
        `👑 ${kingMovesCount} king moves (handled by KingSafety priority)`
      );
    }

    if (hangingMoves.length === 0) {
      this.addLog(
        `✅ No hanging blunders found in ${nonKingMoves.length} non-king moves`
      );
      return {
        shouldEarlyExit: false,
        reasoning: "No hanging pieces detected",
        priority: this.priority,
        diagnostics: { logs: this.analysisLogs },
      };
    }

    if (safeMoves.length === 0) {
      this.addLog(
        `💀 CRITICAL: All ${nonKingMoves.length} non-king moves hang pieces! Forced hanging situation.`
      );
      return {
        shouldEarlyExit: false,
        reasoning:
          "WARNING: All non-king moves hang pieces - forced hanging situation",
        priority: this.priority,
        diagnostics: { logs: this.analysisLogs },
      };
    }

    this.addLog(
      `🛡️ Filtered out ${hangingMoves.length} hanging blunders, keeping ${safeMoves.length} safe moves`
    );

    return this.createFilterResult(
      safeMoves,
      `HANGING PREVENTION: Filtered out ${hangingMoves.length} hanging blunders`,
      {
        hangingMoves: hangingMoves.length,
        safeMoves: safeMoves.length,
        kingMovesCount: kingMovesCount,
        nonKingMovesChecked: nonKingMoves.length,
        logs: this.analysisLogs,
      }
    );
  }

  private addLog(message: string): void {
    this.analysisLogs.push(message);
  }

  /**
   * Simple hanging check: simulate move, check if piece is under attack,
   * check if we can immediately recapture. No compensation logic.
   * NOTE: King moves are pre-filtered out, so this only analyzes non-king pieces.
   */
  private isMovePieceHanging(
    moveInfo: MoveInfo,
    gameState: any
  ): { hanging: boolean; reason: string } {
    try {
      const moveDesc = `[${moveInfo.fromPosition.row},${moveInfo.fromPosition.col}] -> [${moveInfo.move.position.row},${moveInfo.move.position.col}]`;

      // Get the piece being moved
      const piece = MCTSUtils_v2.getPieceAt(
        gameState.board,
        moveInfo.fromPosition
      );
      if (!piece) {
        return { hanging: false, reason: "No piece to move" };
      }

      // NOTE: King moves are pre-filtered out, so this should never be a king
      if (piece.isKing) {
        return { hanging: false, reason: "King move should be pre-filtered" };
      }

      // Simulate the move
      const testState = this.simulateMove(gameState, moveInfo);
      if (!testState) {
        return {
          hanging: true,
          reason: "Simulation failed - assuming hanging",
        };
      }

      // Check if the moved piece is now under attack
      const newPosition = moveInfo.move.position;
      const enemyMoves =
        MCTSMoveGenerationService.getInstance().getAllMoves(testState);

      // Find enemy pieces that can capture our moved piece
      const attackers: string[] = [];

      for (const enemyMove of enemyMoves) {
        if (
          enemyMove.move.position.row === newPosition.row &&
          enemyMove.move.position.col === newPosition.col
        ) {
          const attackerPiece = MCTSUtils_v2.getPieceAt(
            testState.board,
            enemyMove.fromPosition
          );
          if (attackerPiece) {
            const attackerDesc = `${attackerPiece.type} at [${enemyMove.fromPosition.row},${enemyMove.fromPosition.col}]`;
            attackers.push(attackerDesc);
          }
        }
      }

      if (attackers.length === 0) {
        return { hanging: false, reason: "Piece not under attack after move" };
      }

      // CRITICAL FIX: Check if piece is defended BEFORE considering it hanging
      // If defended, enemy capturing = kingslayer suicide, so they won't capture
      if (this.isPieceDefendedAt(testState, newPosition, piece.isLight)) {
        return {
          hanging: false,
          reason: `Piece defended at [${newPosition.row},${newPosition.col}] - enemy capture = kingslayer suicide`,
        };
      }

      // ENHANCED: Piece is under attack - check if we can recapture after the enemy captures our piece
      const canRecapture = this.canRecaptureAfterEnemyCapture(
        testState,
        newPosition,
        enemyMoves
      );

      // ENHANCED LOGGING: Show attackers and recapture analysis
      const result = canRecapture.canRecapture ? "SAFE" : "HANGING";
      this.addLog(
        `${result === "SAFE" ? "✅" : "❌"} ${moveDesc} (${
          piece.type
        }): ${result} - Attackers: [${attackers.join(", ")}] - ${
          canRecapture.reason
        }`
      );

      if (canRecapture.canRecapture) {
        return {
          hanging: false,
          reason: `Can recapture after enemy capture: ${canRecapture.reason}`,
        };
      }

      // Piece is hanging - no recapture possible
      return {
        hanging: true,
        reason: `Hanging to ${attackers.join(", ")} - no recapture possible`,
      };
    } catch (error) {
      const moveDesc = `[${moveInfo.fromPosition.row},${moveInfo.fromPosition.col}] -> [${moveInfo.move.position.row},${moveInfo.move.position.col}]`;
      this.addLog(`❌ Error checking hanging for ${moveDesc}: ${error}`);
      return {
        hanging: true,
        reason: "Error in analysis - assuming hanging for safety",
      };
    }
  }

  /**
   * FIXED LOGIC: Check if we can recapture after the enemy captures our piece
   * This method is only called when we KNOW the piece is under attack
   * 1. Simulate enemy capturing our piece at the new position
   * 2. Check if we can then recapture the enemy piece that captured ours
   */
  private canRecaptureAfterEnemyCapture(
    testState: any,
    ourPieceNewPosition: Position,
    enemyMoves: MoveInfo[]
  ): { canRecapture: boolean; reason: string } {
    // Find enemy moves that can capture our piece at its new position
    const capturingMoves: MoveInfo[] = [];

    for (const enemyMove of enemyMoves) {
      if (
        enemyMove.move.position.row === ourPieceNewPosition.row &&
        enemyMove.move.position.col === ourPieceNewPosition.col
      ) {
        capturingMoves.push(enemyMove);
      }
    }

    // FIXED: This method should only be called when attackers exist
    // If no capturing moves found here, there's a logic error upstream
    if (capturingMoves.length === 0) {
      return {
        canRecapture: false,
        reason: "ERROR: No enemy captures found when expected",
      };
    }

    // For each enemy that can capture our piece, simulate the capture
    // and check if we can recapture
    for (const capturingMove of capturingMoves) {
      const canRecaptureThis = this.simulateEnemyCaptureAndCheckRecapture(
        testState,
        capturingMove,
        ourPieceNewPosition
      );

      if (canRecaptureThis.canRecapture) {
        return canRecaptureThis;
      }
    }

    return {
      canRecapture: false,
      reason: "Cannot recapture any attacking piece after they capture ours",
    };
  }

  /**
   * Simulate the enemy capturing our piece, then check if we can recapture
   */
  private simulateEnemyCaptureAndCheckRecapture(
    currentState: any,
    enemyCapturingMove: MoveInfo,
    ourPiecePosition: Position
  ): { canRecapture: boolean; reason: string } {
    try {
      // Simulate the enemy capturing our piece
      const stateAfterEnemyCapture = cloneFullGameState(currentState);

      // Get the enemy piece
      const enemyPiece = getPieceFromPosition(
        stateAfterEnemyCapture,
        enemyCapturingMove.fromPosition
      );
      if (!enemyPiece) {
        // Enemy piece not found for recapture analysis
        return { canRecapture: false, reason: "Enemy piece not found" };
      }

      // Enhanced recapture analysis with detailed logging
      const enemyPieceDesc = `${enemyPiece.type} at [${enemyCapturingMove.fromPosition.row},${enemyCapturingMove.fromPosition.col}]`;

      // Apply the enemy capture move
      applyMoveWithSnakeRespawn(
        stateAfterEnemyCapture,
        enemyCapturingMove.fromPosition,
        enemyCapturingMove.move,
        enemyPiece
      );

      validateMeadowEffects(stateAfterEnemyCapture);

      // Switch turn back to us
      stateAfterEnemyCapture.isLightTurn = !stateAfterEnemyCapture.isLightTurn;

      // Check if we can capture the enemy piece that just captured ours
      const ourResponseMoves =
        MCTSMoveGenerationService.getInstance().getAllMoves(
          stateAfterEnemyCapture
        );

      const enemyNewPosition = enemyCapturingMove.move.position; // Where the enemy piece is after capturing

      let recaptureOptions = 0;
      const recaptureDetails: string[] = [];
      const nonCaptureAttempts: string[] = [];

      for (const ourMove of ourResponseMoves) {
        if (
          ourMove.move.position.row === enemyNewPosition.row &&
          ourMove.move.position.col === enemyNewPosition.col
        ) {
          // CRITICAL FIX: Check if this is actually a CAPTURE move, not just a movement
          const ourPiece = getPieceFromPosition(
            stateAfterEnemyCapture,
            ourMove.fromPosition
          );
          if (ourPiece && ourMove.move.moveType === MoveType.capture) {
            recaptureOptions++;
            recaptureDetails.push(
              `${ourPiece.type} at [${ourMove.fromPosition.row},${ourMove.fromPosition.col}]`
            );
          } else {
            // Track non-capture moves for debugging
            const moveTypeStr =
              ourMove.move.moveType?.toString() || "undefined";
            nonCaptureAttempts.push(
              `${ourPiece?.type || "Unknown"} at [${ourMove.fromPosition.row},${
                ourMove.fromPosition.col
              }] (${moveTypeStr})`
            );
          }
        }
      }

      // Enhanced debugging for recapture analysis
      const totalMovesToPosition = recaptureOptions + nonCaptureAttempts.length;
      this.addLog(
        `🔍 ${enemyPieceDesc} recapture analysis: ${ourResponseMoves.length} total moves, ${totalMovesToPosition} to position [${enemyNewPosition.row},${enemyNewPosition.col}], ${recaptureOptions} valid captures`
      );

      if (nonCaptureAttempts.length > 0) {
        this.addLog(`🚫 Non-capture moves: ${nonCaptureAttempts.join(", ")}`);
      }

      if (recaptureOptions > 0) {
        return {
          canRecapture: true,
          reason: `Can recapture ${
            enemyPiece.type
          } (${recaptureOptions} options: ${recaptureDetails.join(", ")})`,
        };
      }

      return {
        canRecapture: false,
        reason: `Cannot recapture ${enemyPiece.type} after it captures our piece`,
      };
    } catch (error) {
      this.addLog(`❌ RECAPTURE ERROR: ${error}`);
      return { canRecapture: false, reason: `Simulation error: ${error}` };
    }
  }

  /**
   * CRITICAL: Check if a piece at given position is defended by our forces
   * If defended, enemy king capturing it = immediate kingslayer opportunity
   */
  private isPieceDefendedAt(
    gameState: any,
    position: Position,
    isOurPieceLight: boolean
  ): boolean {
    try {
      // Simulate enemy king capturing our piece at this position
      const testState = cloneFullGameState(gameState);

      // Find enemy king
      const enemyColor = isOurPieceLight ? "black" : "white";
      const enemyKing = this.findKing(testState.board, enemyColor);
      if (!enemyKing) return false;

      // Simulate king capturing our piece
      const kingPiece = this.getPieceAt(testState.board, enemyKing);
      if (!kingPiece) return false;

      // Move king to capture position
      testState.board[enemyKing.row][enemyKing.col].Pieces = [];
      testState.board[position.row][position.col].Pieces = [kingPiece];

      // Switch to our turn
      testState.isLightTurn = isOurPieceLight;

      // Check if we can capture the king at the position
      const moveService = MCTSMoveGenerationService.getInstance();
      const ourMoves = moveService.getAllMoves(testState);

      for (const move of ourMoves) {
        if (
          move.move.position.row === position.row &&
          move.move.position.col === position.col
        ) {
          // We can recapture the king = defended piece!
          return true;
        }
      }

      return false; // We cannot recapture = undefended
    } catch (error) {
      return false; // Assume undefended if simulation fails
    }
  }

  private findKing(board: any[][], color: "white" | "black"): Position | null {
    const isLight = color === "white";

    for (let row = 0; row < board.length; row++) {
      for (let col = 0; col < board[row].length; col++) {
        const square = board[row][col];
        if (square.Pieces && square.Pieces.length > 0) {
          const piece = square.Pieces[0];
          if (piece.isLight === isLight && piece.isKing) {
            return { row, col };
          }
        }
      }
    }
    return null;
  }

  private getPieceAt(board: any[][], position: Position): IPiece | null {
    if (
      position.row < 0 ||
      position.row >= board.length ||
      position.col < 0 ||
      position.col >= board[position.row].length
    ) {
      return null;
    }

    const square = board[position.row][position.col];
    if (!square || !square.Pieces || square.Pieces.length === 0) {
      return null;
    }

    return square.Pieces[0];
  }

  private simulateMove(gameState: any, moveInfo: MoveInfo): any | null {
    try {
      // Use proper deep copy from MCTSUtils
      const testState = cloneFullGameState(gameState);

      // Get the piece properly
      const piece = getPieceFromPosition(testState, moveInfo.fromPosition);
      if (!piece) {
        return null;
      }

      // Apply the move using proper simulation
      applyMoveWithSnakeRespawn(
        testState,
        moveInfo.fromPosition,
        moveInfo.move,
        piece
      );

      // Validate any meadow effects
      validateMeadowEffects(testState);

      return testState;
    } catch (error) {
      this.addLog(`❌ Simulation failed: ${error}`);
      return null;
    }
  }
}

